import {
  Cube, Compass, Drone, MapTrifold, Mountains, Stack, Globe, Path,
  GridFour, Ruler, ShareNetwork, ProjectorScreen, ChartLine,
} from "@phosphor-icons/react";

// Monogram blocks for each tool — avoids any third-party logo/trademark issues
// and stays consistent with the cad4surveys editorial-technical aesthetic.
export const SOFTWARE = [
  { name: "AutoCAD",        mono: "AC",  category: "2D / 3D Drafting",        icon: Cube,             tone: "blue" },
  { name: "AutoCAD LT",     mono: "LT",  category: "2D Drafting",             icon: Cube,             tone: "blue" },
  { name: "Civil 3D",       mono: "C3D", category: "Civil & Survey",          icon: Path,             tone: "blue" },
  { name: "BricsCAD",       mono: "BC",  category: "DWG-native CAD",          icon: Cube,             tone: "red"  },
  { name: "progeCAD",       mono: "PG",  category: "DWG-compatible CAD",      icon: Cube,             tone: "amber" },
  { name: "IntelliCAD",     mono: "iC",  category: "DWG-compatible CAD",      icon: Cube,             tone: "green" },
  { name: "Carlson Survey", mono: "CS",  category: "Survey & Civil",          icon: Compass,          tone: "red"  },
  { name: "Carlson Civil",  mono: "CC",  category: "Civil Engineering",       icon: Path,             tone: "red"  },
  { name: "MicroStation",   mono: "MS",  category: "Bentley CAD",             icon: Cube,             tone: "indigo" },
  { name: "OpenRoads",      mono: "OR",  category: "Bentley Civil",           icon: Path,             tone: "indigo" },
  { name: "Trimble Business Center", mono: "TBC", category: "Survey Office",  icon: Compass,          tone: "amber" },
  { name: "Topcon MAGNET",  mono: "TM",  category: "Survey Office",           icon: Compass,          tone: "amber" },
  { name: "Leica Infinity", mono: "LI",  category: "Survey Office",           icon: Compass,          tone: "red"  },
  { name: "Global Mapper",  mono: "GM",  category: "GIS / Terrain",           icon: Globe,            tone: "green" },
  { name: "ArcGIS Pro",     mono: "AG",  category: "GIS",                     icon: MapTrifold,       tone: "indigo" },
  { name: "QGIS",           mono: "QG",  category: "GIS (open-source)",       icon: MapTrifold,       tone: "green" },
  { name: "Pix4D",          mono: "P4",  category: "Photogrammetry / Drone",  icon: Drone,            tone: "blue" },
  { name: "Agisoft Metashape", mono: "AM", category: "Photogrammetry",        icon: Drone,            tone: "amber" },
  { name: "Recap Pro",      mono: "RC",  category: "Point Cloud",             icon: Stack,            tone: "blue" },
  { name: "CloudCompare",   mono: "CL",  category: "Point Cloud (open-source)", icon: Stack,          tone: "green" },
  { name: "StarNet",        mono: "SN",  category: "Network Adjustment",      icon: ShareNetwork,     tone: "indigo" },
  { name: "LISCAD",         mono: "LS",  category: "Survey Drafting",         icon: Ruler,            tone: "red"  },
  { name: "12d Model",      mono: "12",  category: "Civil & Survey",          icon: Mountains,        tone: "amber" },
  { name: "Terramodel",     mono: "TR",  category: "Survey & Site Design",    icon: GridFour,         tone: "indigo" },
  { name: "Adobe Acrobat",  mono: "PDF", category: "Plan Sets & Exhibits",    icon: ProjectorScreen,  tone: "red"  },
  { name: "Microsoft Excel", mono: "XL", category: "Coordinate / Tabulation", icon: ChartLine,        tone: "green" },
];

const TONE = {
  blue:   "border-[#0044FF] text-[#0044FF]",
  red:    "border-red-700 text-red-700",
  amber:  "border-amber-700 text-amber-700",
  green:  "border-emerald-700 text-emerald-700",
  indigo: "border-indigo-700 text-indigo-700",
};

export default function SoftwareWeUse({ variant = "full" }) {
  if (variant === "compact") {
    return (
      <div className="flex flex-wrap gap-2" data-testid="software-compact">
        {SOFTWARE.map((s) => (
          <span
            key={s.name}
            className={`inline-flex items-center gap-2 px-2.5 py-1.5 border bg-white text-xs ${TONE[s.tone]}`}
            title={s.category}
          >
            <span className={`font-mono font-bold text-[10px] tracking-tight ${TONE[s.tone].split(" ")[1]}`}>
              {s.mono}
            </span>
            <span className="text-zinc-700">{s.name}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <section
      className="bg-white py-24 md:py-32 border-y border-zinc-200"
      data-testid="software-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-6">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              Software & toolchain
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05]">
              Fluent in every CAD & survey platform you use.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="text-lg text-zinc-600 leading-relaxed">
              Send us your file in any format — we draft natively in your platform with your
              template, layers, and standards. No conversion artifacts, no surprises.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-zinc-200 border border-zinc-200"
          data-testid="software-grid"
        >
          {SOFTWARE.map((s) => (
            <div
              key={s.name}
              className="bg-white p-5 hover:bg-zinc-50 transition-colors flex flex-col items-start gap-3"
              data-testid={`software-${s.mono.toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-11 h-11 border bg-white flex items-center justify-center font-mono font-bold text-[11px] tracking-tighter ${TONE[s.tone]}`}
                  aria-hidden="true"
                >
                  {s.mono}
                </div>
                <s.icon size={20} weight="duotone" className="text-zinc-400" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-950 leading-tight">{s.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {s.category}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-zinc-500 font-mono">
          Don't see your software? We adapt to your toolchain — just ask.
        </p>
      </div>
    </section>
  );
}
